import { useEffect, useState } from "preact/hooks";
import { config } from "../config";

export const TRACKED_PARAMS = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
	"gclid",
	"fbclid",
	"msclkid",
	"ttclid",
] as const;

export type TrackedParam = (typeof TRACKED_PARAMS)[number];

export type PageParams = {
	campaign_id: string | null;
} & Record<TrackedParam, string | null>;

const resolveBackgroundColor = (rawColor: string | null): string | null => {
	if (!rawColor) {
		return null;
	}

	const decoded = rawColor.trim();
	if (!decoded) {
		return null;
	}

	if (/^[0-9a-fA-F]{6}$/.test(decoded)) {
		return `#${decoded}`;
	}

	return null;
};

const MAX_HEADING_LEN = 200;

const resolveHeading = (raw: string | null): string | null => {
	if (!raw) {
		return null;
	}
	const decoded = raw.trim();
	if (!decoded) {
		return null;
	}
	const cleaned = [...decoded]
		.filter((ch) => {
			const c = ch.codePointAt(0);
			if (c === undefined) return false;
			return c > 0x1f && c !== 0x7f;
		})
		.join("");
	if (!cleaned) {
		return null;
	}
	if (cleaned.length > MAX_HEADING_LEN) {
		return cleaned.slice(0, MAX_HEADING_LEN);
	}
	return cleaned;
};

const emptyTrackedParams = (): Record<TrackedParam, string | null> =>
	TRACKED_PARAMS.reduce(
		(acc, key) => {
			acc[key] = null;
			return acc;
		},
		{} as Record<TrackedParam, string | null>,
	);

export function usePageParams() {
	const [urlParams, setUrlParams] = useState<PageParams>({
		campaign_id: null,
		...emptyTrackedParams(),
	});
	const [isValidPage, setIsValidPage] = useState<boolean | null>(null);
	const [backgroundColorOverride, setBackgroundColorOverride] = useState<
		string | null
	>(null);
	const [logoUrl, setLogoUrl] = useState<string | null>(null);
	const [headingOverride, setHeadingOverride] = useState<string | null>(null);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		// Must be numeric — matches campaigns.id and the campaign identifier sent to /voice/start_call.
		const campaignId = params.get("campaign_id");
		setBackgroundColorOverride(
			resolveBackgroundColor(params.get("background_color")),
		);
		const logoId = params.get("logo");
		setLogoUrl(
			logoId ? `${config.cdn.base_url}/campaign_logos/${logoId}` : null,
		);
		setHeadingOverride(resolveHeading(params.get("heading")));

		if (campaignId && /^\d+$/.test(campaignId.trim())) {
			const tracked = emptyTrackedParams();
			for (const key of TRACKED_PARAMS) {
				const value = params.get(key);
				if (value?.trim()) tracked[key] = value.trim();
			}
			setUrlParams({
				campaign_id: campaignId.trim(),
				...tracked,
			});
			setIsValidPage(true);
			return;
		}

		setIsValidPage(false);
	}, []);

	useEffect(() => {
		const PREFIX = "jade-ztron-form";
		const onMessage = (event: MessageEvent) => {
			if (event.source !== window.parent) return;
			const data = event.data;
			if (!data || typeof data !== "object") return;
			if (data.type !== `${PREFIX}-params`) return;
			const payload = data.payload;
			if (!payload || typeof payload !== "object") return;
			const updates: Partial<PageParams> = {};
			for (const key of TRACKED_PARAMS) {
				const value = payload[key];
				if (typeof value === "string" && value.trim()) {
					updates[key] = value.trim();
				}
			}
			const campaignIdValue = payload.campaign_id;
			if (
				typeof campaignIdValue === "string" &&
				/^\d+$/.test(campaignIdValue.trim())
			) {
				updates.campaign_id = campaignIdValue.trim();
			}
			if (Object.keys(updates).length === 0) return;
			setUrlParams((prev) => ({ ...prev, ...updates }));
		};
		window.addEventListener("message", onMessage);
		if (window.parent && window.parent !== window) {
			window.parent.postMessage({ type: `${PREFIX}-ready` }, "*");
		}
		return () => window.removeEventListener("message", onMessage);
	}, []);

	return {
		urlParams,
		isValidPage,
		backgroundColorOverride,
		logoUrl,
		headingOverride,
	};
}
